/**
 * The Business Unit create / edit dialog (f5.1, ownership added here).
 *
 * Built through f2.2's `Modal` on the prototype's own `modal-scrim` /
 * `modal-head` / `modal-body` / `modal-foot` shapes.
 *
 * Three fieldsets: `Identity`, `Feed matching` and `Ownership`.
 *
 * **`Identity` is laid out as three rows**, because the parent is the field
 * that decides what the other two mean: the Business Unit Group on its own
 * row, then `Code` and `Name` side by side, then the reporting currency.
 *
 * **Deliberate departures**, each argued rather than silent:
 *
 * | Element | What is built | Why |
 * | --- | --- | --- |
 * | A `Status` fieldset | **Absent** | Status is not a property you type when you create a unit: a new unit is Active, and the only transitions afterwards are deactivate and reactivate, which the table offers as row actions where the child guard and the static audit action live. A select here would record a deactivation as an ordinary edit, and could set a unit inactive at birth -- one mistake enabled, nothing gained. f4.1's dialog omitted it on the same ground |
 * | `CEM` and `DM` owner pickers | **Absent** | Neither role is held at this level. The brief's roles matrix, f4.2's spec and the server's `ROLE_NODE_TYPES` all agree: `DEM` and `CEM` belong to a Business Unit Group, `CM` to a Business Unit, `DM` to a Client and `PROJECT_HEAD` to an Engagement. Offering either here would produce `409 DELIVERY_ROLE_LEVEL_MISMATCH` on every save |
 * | `required` on the CM | Not required | Ownership is an assignment, and a unit is routinely recorded before anybody has decided who will run it. On a fresh installation there may be no assignable account at all |
 * | A Business Unit Group select on the EDIT dialog | Read-only on edit | The parent is half the uniqueness key, so a move either collides with a code in the target group or silently frees one in the source -- and once f9.1 ships, re-parenting is a bulk permission change wearing the costume of a dropdown. Deferred, not forbidden: it would arrive as its own transition route with its own audit action |
 * | The prototype's CM hint *"Commercial Manager"* | -- | CM is **Client Manager**. The glossary, the brief and f4.2's seeded `delivery_role` row all agree; *"Commercial Manager"* occurs exactly once in the whole repository, in that hint |
 *
 * **Ownership is written as assignments, not as columns.** There is no `cm_id`
 * on the unit: naming an owner is a `POST` to the assignments endpoint, and
 * replacing one is end-then-add, both audited and both preserved. This dialog
 * issues those requests *after* the unit itself is saved, so a refused
 * assignment never loses the unit.
 *
 * **No control holds a map from code to label.** The currency and delivery-role
 * selects are populated from `GET /lookups`, so a currency an Admin inserts
 * becomes selectable with no change here, and a role renamed in the lookup
 * renames itself.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

import type { BusinessUnit, BusinessUnitGroup } from '@/api/client';
import {
  useAddBusinessUnit,
  useAssignOwner,
  useAssignableUsers,
  useEndAssignment,
  useLookupCatalog,
  useUpdateBusinessUnit,
} from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';
import { DELIVERY_ROLE_TYPE, ownerFor } from '@/routes/business-unit-groups/ownershipView';

import { UNIT_ROLE_CODES, generalMessage, problemFor, type UnitRoleCode } from './unitView';

/** The lookup type this dialog's currency select reads. Values are the server's. */
const CURRENCY_TYPE = 'CURRENCY';

/** The level these assignments are recorded against. */
const NODE_TYPE = 'BUSINESS_UNIT';

/** The account holding each role when the dialog opened, keyed by role code. */
type Ownership = Partial<Record<UnitRoleCode, string>>;

function currentOwnership(unit: BusinessUnit | null): Ownership {
  if (!unit) return {};
  return Object.fromEntries(
    UNIT_ROLE_CODES.map((roleCode) => [roleCode, ownerFor(unit, roleCode)?.user_id ?? '']),
  );
}

/** One spelling per line, as the prototype's textarea promises. */
function linesToAliases(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function UnitDialog({
  unit,
  groups,
  onClose,
}: {
  /** null for a create. */
  unit: BusinessUnit | null;
  /** The ACTIVE groups a create may name. An inactive one is refused server-side. */
  groups: readonly BusinessUnitGroup[];
  onClose: () => void;
}) {
  const editing = unit !== null;
  const create = useAddBusinessUnit();
  const update = useUpdateBusinessUnit();
  const assign = useAssignOwner();
  const endAssignment = useEndAssignment();
  const candidates = useAssignableUsers();
  const catalog = useLookupCatalog();

  const [groupId, setGroupId] = useState(unit?.business_unit_group.id ?? groups[0]?.id ?? '');
  const [code, setCode] = useState(unit?.code ?? '');
  const [name, setName] = useState(unit?.name ?? '');
  const [currencyCode, setCurrencyCode] = useState(unit?.reporting_currency.code ?? '');
  const [aliasText, setAliasText] = useState((unit?.aliases ?? []).join('\n'));
  const [owners, setOwners] = useState<Ownership>(() => currentOwnership(unit));

  const users = candidates.data?.users ?? [];
  const before = useMemo(() => currentOwnership(unit), [unit]);

  const currencies = useMemo(
    () =>
      (catalog.data?.types.find((type) => type.code === CURRENCY_TYPE)?.values ?? []).filter(
        // Only active values are offered for a NEW assignment; the list and
        // detail screens always render the STORED value, active or not. f2.2's
        // consumer rule, applied without restatement.
        (value) => value.is_active,
      ),
    [catalog.data],
  );
  const deliveryRoles = useMemo(
    () => catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)?.values ?? [],
    [catalog.data],
  );

  /**
   * The role id for a role code, from the catalogue.
   *
   * The code is the only thing this screen names; every id and every label is
   * the server's, so a role renamed in the lookup renames itself here.
   */
  const roleIdFor = (roleCode: UnitRoleCode): string | undefined =>
    deliveryRoles.find((role) => role.code === roleCode && role.is_active)?.id;

  const failure = create.error ?? update.error ?? assign.error ?? endAssignment.error;
  const busy =
    create.isPending || update.isPending || assign.isPending || endAssignment.isPending;

  /**
   * Write the ownership changes, one role at a time.
   *
   * Only a role whose chosen account DIFFERS from the one already recorded is
   * touched: re-saving a dialog nobody edited must not append an assignment
   * that says the same thing, because every write is audited and a log full of
   * no-op assignments is a log nobody reads.
   */
  async function applyOwnership(unitId: string) {
    for (const roleCode of UNIT_ROLE_CODES) {
      const chosen = owners[roleCode] ?? '';
      if (chosen === (before[roleCode] ?? '')) continue;

      const held = unit ? ownerFor(unit, roleCode) : undefined;
      if (held) {
        // End then add: nothing is deleted, and who held the role before stays
        // on the record and in the audit log.
        await endAssignment.mutateAsync({
          assignmentId: held.assignment_id,
          nodeType: NODE_TYPE,
          nodeId: unitId,
        });
      }

      const deliveryRoleId = chosen ? roleIdFor(roleCode) : undefined;
      if (!chosen || !deliveryRoleId) continue;

      await assign.mutateAsync({
        user_id: chosen,
        delivery_role_id: deliveryRoleId,
        node_type: NODE_TYPE,
        node_id: unitId,
      });
    }
  }

  async function submit() {
    const aliases = linesToAliases(aliasText);
    try {
      // The unit is saved first. If an assignment is then refused the dialog
      // stays open with the refusal on screen, and the unit is not lost.
      const saved = editing
        ? await update.mutateAsync({
            unitId: unit.id,
            change: {
              name,
              // Only what the dialog can change. `code` and the parent are not
              // accepted by the server on a PATCH, and sending either would be
              // a `422` rather than a silent no-op.
              ...(currencyCode ? { reporting_currency_code: currencyCode } : {}),
              aliases,
            },
          })
        : await create.mutateAsync({
            business_unit_group_id: groupId,
            code,
            name,
            // Omitted rather than guessed: the server defaults it from f2.1's
            // `general.default_business_unit_reporting_currency` setting, and
            // no currency literal exists anywhere in this application.
            ...(currencyCode ? { reporting_currency_code: currencyCode } : {}),
            ...(aliases.length ? { aliases } : {}),
          });

      await applyOwnership(saved.id);
      onClose();
    } catch {
      // The mutation that failed is holding the refusal, and `failure` above
      // renders it. Swallowing the rejection here only stops it becoming an
      // unhandled one; nothing is hidden from the reader.
    }
  }

  return (
    <Modal
      title={editing ? 'Edit Business Unit' : 'New Business Unit'}
      subtitle="The unit of COM data entry. Its reporting currency drives every forecast line on it"
      onClose={onClose}
      onSubmit={() => {
        void submit();
      }}
      busy={busy}
      submitLabel="Save Business Unit"
      submitIcon={<Check aria-hidden="true" />}
      // The prototype's `modal wide`: one of the fieldsets is a full-width
      // aliases textarea that a Kimble filter banner is pasted into whole.
      wide
      layout="plain"
      // Shown only when the server refused without naming a field, so a
      // field-level problem is never duplicated at the top of the dialog.
      message={generalMessage(
        failure,
        'business_unit_group_id',
        'code',
        'name',
        'reporting_currency_code',
        'aliases',
        'user_id',
        'delivery_role_id',
      )}
    >
      <fieldset className="mb-4">
        <div className="fieldset-title">Identity</div>
        {/* Three rows: the parent, then the unit's own two identifiers, then
            the currency. `span` makes a field take the whole row. */}
        <div className="grid grid-2">
          <DialogField
            label="Business Unit Group"
            htmlFor="bu-group"
            span
            problem={problemFor(failure, 'business_unit_group_id')}
            hint={
              editing
                ? 'Fixed at creation. A unit does not move between groups'
                : 'Required. Only active groups can be chosen'
            }
          >
            <select
              id="bu-group"
              value={groupId}
              required
              // Read-only on edit rather than removed, so the value stays
              // visible where it was chosen.
              disabled={editing}
              onChange={(event) => setGroupId(event.target.value)}
            >
              {editing ? (
                <option value={unit.business_unit_group.id}>
                  {unit.business_unit_group.name}
                </option>
              ) : (
                <>
                  <option value="">Choose a group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </DialogField>

          <DialogField
            label="Code"
            htmlFor="bu-code"
            problem={problemFor(failure, 'code')}
            hint={
              editing
                ? 'Immutable. Two feeds and the import match on it'
                : 'Unique within the group. A single internal space is allowed'
            }
          >
            <input
              id="bu-code"
              type="text"
              value={code}
              readOnly={editing}
              required
              placeholder="US 01"
              onChange={(event) => setCode(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="Name"
            htmlFor="bu-name"
            problem={problemFor(failure, 'name')}
            hint='Unique across every group: the Kimble "Proposal: Unit" column and the actuals export both resolve a unit by name alone'
          >
            <input
              id="bu-name"
              type="text"
              value={name}
              required
              placeholder="CU US Asset 01"
              onChange={(event) => setName(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="Reporting currency"
            htmlFor="bu-currency"
            problem={problemFor(failure, 'reporting_currency_code')}
            hint="Drives the currency of every forecast line on this unit"
          >
            <select
              id="bu-currency"
              value={currencyCode}
              disabled={catalog.isPending}
              onChange={(event) => setCurrencyCode(event.target.value)}
            >
              {/* The empty option is the DEFAULT, not "none": the server reads
                  it from the platform setting, so the dialog does not have to
                  know what it is. */}
              <option value="">Use the platform default</option>
              {currencies.map((value) => (
                <option key={value.id} value={value.code}>
                  {value.code} &mdash; {value.name}
                </option>
              ))}
            </select>
          </DialogField>
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <div className="fieldset-title">Feed matching</div>
        <div className="grid grid-2">
          <DialogField
            label="Kimble unit aliases"
            htmlFor="bu-alias"
            span
            problem={problemFor(failure, 'aliases')}
            hint="Every spelling listed here resolves to this unit when a feed is staged. Saving replaces the whole set"
          >
            <textarea
              id="bu-alias"
              value={aliasText}
              placeholder='One legacy "Proposal: Unit" spelling per line'
              onChange={(event) => setAliasText(event.target.value)}
            />
          </DialogField>
        </div>
      </fieldset>

      <fieldset>
        <div className="fieldset-title">Ownership</div>
        <div className="grid grid-2">
          {UNIT_ROLE_CODES.map((roleCode) => (
            <DialogField
              key={roleCode}
              label={deliveryRoles.find((role) => role.code === roleCode)?.name ?? roleCode}
              htmlFor={`bu-owner-${roleCode}`}
              problem={problemFor(failure, 'user_id')}
              hint={
                candidates.isPending
                  ? 'Loading the accounts'
                  : users.length === 0
                    ? 'No account can be named yet. Add one on the Users screen first'
                    : 'Optional. Ownership decides access'
              }
            >
              <select
                id={`bu-owner-${roleCode}`}
                value={owners[roleCode] ?? ''}
                disabled={candidates.isPending || users.length === 0}
                onChange={(event) =>
                  setOwners((current) => ({ ...current, [roleCode]: event.target.value }))
                }
              >
                <option value="">No {roleCode} yet</option>
                {/* The address disambiguates two accounts sharing a display
                    name, exactly as the assign dialog's picker does. */}
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name} ({user.email})
                  </option>
                ))}
              </select>
            </DialogField>
          ))}
        </div>
      </fieldset>
    </Modal>
  );
}
